/**
 * ProjectCard component
 * Displays a project card with basic info
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import Card from '../common/Card'
import Button from '../common/Button'

const ProjectCard = ({ project, onDelete, onRestore, isDeleted = false }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false)
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800'
      case 'editor':
        return 'bg-blue-100 text-blue-800'
      case 'viewer':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleDeleteClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setShowDeleteConfirm(true)
  }

  const handleCancelDelete = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setShowDeleteConfirm(false)
  }

  const handleConfirmDelete = (e) => {
    e.preventDefault()
    e.stopPropagation()
    onDelete(project.id)
    setShowDeleteConfirm(false)
  }

  const handleRestoreClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setShowRestoreConfirm(true)
  }

  const handleCancelRestore = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setShowRestoreConfirm(false)
  }

  const handleConfirmRestore = (e) => {
    e.preventDefault()
    e.stopPropagation()
    onRestore(project.id)
    setShowRestoreConfirm(false)
  }

  const isOwner = project.user_role === 'owner'

  return (
    <div className="relative">
      <Link
        to={isDeleted ? '#' : `/projects/${project.id}`}
        onClick={isDeleted ? (e) => e.preventDefault() : undefined}
      >
        <Card
          className={`transition-shadow ${isDeleted ? 'bg-gray-50 opacity-75' : 'hover:shadow-lg cursor-pointer'} ${showDeleteConfirm || showRestoreConfirm ? 'ring-2 ring-red-300' : ''}`}
        >
          <div className="flex items-start justify-between mb-3">
            <h3
              className={`text-lg font-semibold ${isDeleted ? 'text-gray-500' : 'text-gray-900'}`}
            >
              {project.name}
            </h3>
            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(project.user_role)}`}
              >
                {project.user_role}
              </span>
              {!isDeleted && isOwner && !showDeleteConfirm && (
                <button
                  onClick={handleDeleteClick}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Delete project"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
              {isDeleted && isOwner && !showRestoreConfirm && (
                <button
                  onClick={handleRestoreClick}
                  className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                  title="Restore project"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {project.description && (
            <p className="text-sm text-gray-600 mb-4 line-clamp-2">{project.description}</p>
          )}

          <div className="flex items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              <span>
                {project.file_count || 0} {project.file_count === 1 ? 'file' : 'files'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <span>
                {project.member_count || 0} {project.member_count === 1 ? 'member' : 'members'}
              </span>
            </div>
          </div>
        </Card>
      </Link>

      {/* Inline Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-red-50 border-2 border-red-300 rounded-lg p-4 flex flex-col justify-center items-center gap-3 z-10">
          <p className="text-sm text-red-800 text-center font-medium">
            Delete &quot;{project.name}&quot;?
          </p>
          <p className="text-xs text-red-600 text-center">This will be recoverable for 30 days</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancelDelete}
              className="bg-white hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </div>
        </div>
      )}

      {/* Inline Restore Confirmation */}
      {showRestoreConfirm && (
        <div className="absolute inset-0 bg-green-50 border-2 border-green-300 rounded-lg p-4 flex flex-col justify-center items-center gap-3 z-10">
          <p className="text-sm text-green-800 text-center font-medium">
            Restore &quot;{project.name}&quot;?
          </p>
          <p className="text-xs text-green-600 text-center">This project will be active again</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancelRestore}
              className="bg-white hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleConfirmRestore}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Restore
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

ProjectCard.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    user_role: PropTypes.oneOf(['owner', 'editor', 'viewer']).isRequired,
    file_count: PropTypes.number,
    member_count: PropTypes.number,
    is_deleted: PropTypes.bool,
    deleted_at: PropTypes.string,
  }).isRequired,
  onDelete: PropTypes.func,
  onRestore: PropTypes.func,
  isDeleted: PropTypes.bool,
}

export default ProjectCard
